import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useSpring, useTransform } from "framer-motion";
import { api, fmtMoney } from "@/lib/apiClient";
import ThermalCore from "@/pages/dashboard/ThermalCore";
import AmbientContextStrip from "@/pages/dashboard/AmbientContextStrip";
import {
  ArrowUpRight, AlertTriangle, Coins, Package, Wrench,
  Building2, ShoppingCart, Receipt, ChevronRight,
} from "lucide-react";

/**
 * RoleCenter — HVAC × India ERP dashboard (LIGHT theme).
 * Refined, calm, cinematic — nothing like any other ERP in the world.
 */
export default function RoleCenter() {
  const [data, setData] = useState(null);
  const [budgets, setBudgets] = useState([]);

  const load = useCallback(async () => {
    const [d, b] = await Promise.all([
      api.get("/dashboard/summary").catch(() => ({ data: null })),
      api.get("/budgets/status").catch(() => ({ data: [] })),
    ]);
    setData(d.data);
    setBudgets(b.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingState />;

  const k = data.kpis;
  const heatLoad = (k.total_expenses || 0) + (k.overdue_amount || 0) + (k.po_open_value || 0);
  const coolCap  = (k.total_revenue || 0) + Math.max(0, k.profit || 0);
  const marginPct = k.total_revenue > 0 ? Math.round((k.profit / k.total_revenue) * 100) : 0;

  const budgetsBreach = budgets.filter((r) => r.status === "breach").length;
  const budgetsWarn   = budgets.filter((r) => r.status === "warn").length;

  const alerts = buildAlerts(k, budgetsBreach);
  const isEmpty = (k.total_revenue || 0) === 0 && (k.total_expenses || 0) === 0;

  return (
    <div className="thermal-core-scope tc-paper min-h-screen text-stone-900" data-testid="role-center">
      {/* Ambient gradient wash */}
      <div className="pointer-events-none absolute inset-0 -z-0"
           style={{ background: "radial-gradient(ellipse at 15% -10%, rgba(234,88,12,0.10), transparent 55%), radial-gradient(ellipse at 90% 110%, rgba(8,145,178,0.08), transparent 45%)" }} />

      {/* ---- Hero header ---- */}
      <div className="relative border-b border-stone-200/70 bg-white/60 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-8 py-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-tc-mono tracking-[0.32em] text-stone-500">
              CELSIUS · HVAC · ROLE CENTER
            </div>
            <h1 className="font-tc-display text-[42px] md:text-[52px] font-black leading-none tracking-tight text-stone-900 mt-2">
              {greeting()}<span className="text-[#EA580C]">.</span>
            </h1>
            <div className="text-[13.5px] text-stone-600 mt-3 max-w-xl leading-relaxed">
              A calm, single-glance view of your business — heat on the outside, flow on the inside. Balance them, and profit follows.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <QuickLink to="/finance/balance-sheet" label="Balance Sheet" />
            <QuickLink to="/profit" label="P&L" />
            <QuickLink to="/finance/budgets" label="Budgets" />
          </div>
        </div>
      </div>

      <div className="relative max-w-[1600px] mx-auto px-8 py-8 space-y-6">
        {/* ==================== ROW 1 ==================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Hero — Thermal Core + KPI stack */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-8"
          >
            <div className="relative rounded-3xl border border-stone-200 bg-white/90 backdrop-blur shadow-[0_1px_2px_rgba(0,0,0,0.04),0_25px_60px_-30px_rgba(234,88,12,0.25)] overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 p-8 lg:p-10">
                <div className="lg:col-span-3">
                  {isEmpty ? <CalibratingCore /> : (
                    <ThermalCore heatLoad={heatLoad} coolingCapacity={coolCap} marginPct={marginPct} />
                  )}
                </div>
                <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-1 gap-3">
                  <KpiSlab label="Revenue"        value={k.total_revenue}      tone="flow"  sub="Total paid + sales"    to="/profit"     testid="kpi-revenue" />
                  <KpiSlab label="Net Profit"     value={k.profit}             tone="brand" sub={`${marginPct}% margin`} to="/profit"     testid="kpi-profit" />
                  <KpiSlab label="Outstanding AR" value={k.outstanding}        tone="warn"  sub={`${k.overdue_count || 0} overdue`} to="/aging" testid="kpi-ar" />
                  <KpiSlab label="Open PO Value"  value={k.po_open_value || 0} tone="mute"  sub={`${k.open_pos_count || 0} pending`} to="/vendor-pos" testid="kpi-po" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Ambient + Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-4 flex flex-col gap-6"
          >
            <AmbientContextStrip />
            <AlertStrip alerts={alerts} />
          </motion.div>
        </div>

        {/* ==================== ROW 2 ==================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-8 rounded-3xl border border-stone-200 bg-white/90 backdrop-blur shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] font-tc-mono tracking-[0.28em] text-stone-500">SECTORS · NEXT ACTIONS</div>
                <div className="font-tc-display text-[20px] font-bold text-stone-900 mt-1">One tap into what needs you</div>
              </div>
              <Link to="/dashboard" className="text-[11px] text-stone-500 hover:text-[#EA580C] font-tc-mono">Legacy view →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ActionModuleBar icon={Coins} label="Sales & Receivables" metric={fmtMoney(k.total_revenue)} sub={`${k.invoice_count || 0} invoices · ${k.overdue_count || 0} overdue`} to="/invoices" testid="action-sales" />
              <ActionModuleBar icon={ShoppingCart} label="Procurement" metric={fmtMoney(k.po_open_value || 0)} sub={`${k.open_pos_count || 0} open PO · ${k.grn_count || 0} GRN`} to="/vendor-pos" testid="action-procurement" />
              <ActionModuleBar icon={Package} label="Warehouse & Site" metric={fmtMoney(k.inventory_value || 0)} sub={`${k.warehouse_count || 0} sites · ${k.low_stock_count || 0} low`} to="/warehouses" testid="action-warehouse" alert={k.low_stock_count > 0} />
              <ActionModuleBar icon={Wrench} label="Service & Support" metric={`${k.open_tickets || 0} open`} sub={`${k.tickets_overdue || 0} SLA breached · ${k.ppm_due || 0} PPM due`} to="/service/tickets" testid="action-service" alert={k.tickets_overdue > 0} />
              <ActionModuleBar icon={Building2} label="Finance" metric={fmtMoney(k.profit)} sub={`${budgetsBreach} budgets breached · ${budgetsWarn} warn`} to="/profit" testid="action-finance" alert={budgetsBreach > 0} />
              <ActionModuleBar icon={Receipt} label="India Taxation" metric={fmtMoney(k.gst_collected || 0)} sub={`GST · TDS ${fmtMoney(k.tds_deducted || 0)}`} to="/finance/opening-balances" testid="action-taxation" />
            </div>
          </motion.div>

          {/* Live feed */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
            className="lg:col-span-4 rounded-3xl border border-stone-200 bg-white/90 backdrop-blur shadow-sm overflow-hidden"
            data-testid="live-feed"
          >
            <div className="px-6 py-5 border-b border-stone-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-tc-mono tracking-[0.28em] text-stone-500">LIVE FEED</div>
                <div className="font-tc-display text-[18px] font-bold text-stone-900 mt-1">Recent activity</div>
              </div>
              <span className="w-2 h-2 rounded-full bg-[#0891B2] tc-glow" />
            </div>
            <div className="divide-y divide-stone-200 max-h-[460px] overflow-y-auto">
              {(data.recent_invoices || []).slice(0, 5).map((i) => (
                <Link key={`inv-${i.id}`} to="/invoices"
                  className="tc-blip flex items-start gap-3 px-6 py-3.5 hover:bg-stone-50 transition-colors group">
                  <div className="w-[3px] self-stretch bg-[#0891B2] rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-tc-mono text-stone-500 uppercase tracking-widest">Invoice · {i.status}</div>
                    <div className="text-[13.5px] text-stone-900 font-semibold truncate">{i.invoice_number} · {i.client_name}</div>
                  </div>
                  <div className="font-tc-mono text-[13.5px] font-bold text-stone-900 shrink-0">{fmtMoney(i.total)}</div>
                </Link>
              ))}
              {(data.recent_projects || []).slice(0, 5).map((p) => (
                <Link key={`proj-${p.id}`} to={`/projects/${p.id}`}
                  className="tc-blip flex items-start gap-3 px-6 py-3.5 hover:bg-stone-50 transition-colors group">
                  <div className="w-[3px] self-stretch bg-[#EA580C] rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-tc-mono text-stone-500 uppercase tracking-widest">Project · {p.status?.replace("_", " ")}</div>
                    <div className="text-[13.5px] text-stone-900 font-semibold truncate">{p.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">{p.client_name}</div>
                  </div>
                </Link>
              ))}
              {(data.recent_invoices || []).length === 0 && (data.recent_projects || []).length === 0 && (
                <div className="px-6 py-10 text-center text-[12px] text-stone-500 font-tc-mono">
                  NO ACTIVITY YET · POST YOUR FIRST INVOICE
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer signature */}
      <div className="border-t border-stone-200/70 mt-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-[1600px] mx-auto px-8 py-4 flex items-center justify-between text-[10px] font-tc-mono text-stone-500">
          <div>THERMAL CORE · v2 · Load {heatLoad + coolCap > 0 ? (heatLoad / (heatLoad + coolCap) * 100).toFixed(1) : 0}%</div>
          <div>CELSIUS · POWERED BY EMERGENT</div>
        </div>
      </div>
    </div>
  );
}

// ==================== sub-components ====================
function greeting() {
  const h = new Date().toLocaleString("en-IN", { hour: "numeric", hour12: false, timeZone: "Asia/Kolkata" });
  const hr = parseInt(h, 10);
  if (hr < 5) return "Late shift";
  if (hr < 12) return "Good morning";
  if (hr < 17) return "Good afternoon";
  if (hr < 21) return "Good evening";
  return "Good night";
}

function KpiSlab({ label, value, sub, tone, to, testid }) {
  const tones = {
    flow:  { border: "border-cyan-200",  bg: "bg-white",            accent: "text-cyan-700" },
    brand: { border: "border-orange-300", bg: "bg-white",           accent: "text-[#EA580C]" },
    warn:  { border: "border-amber-200",  bg: "bg-white",           accent: "text-amber-700" },
    mute:  { border: "border-stone-200",  bg: "bg-white",           accent: "text-stone-900" },
  };
  const t = tones[tone] || tones.mute;

  const spring = useSpring(0, { stiffness: 60, damping: 15 });
  const disp = useTransform(spring, (v) => fmtMoney(Math.round(v)));
  useEffect(() => { spring.set(value || 0); }, [value, spring]);

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative ${t.bg} border ${t.border} p-4 rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-[#EA580C]/50 block`}
    >
      <div className="text-[9.5px] font-tc-mono tracking-[0.28em] text-stone-500 uppercase">{label}</div>
      <motion.div className={`font-tc-display text-[24px] md:text-[26px] font-black tracking-tight mt-1 ${t.accent}`}>
        <motion.span>{disp}</motion.span>
      </motion.div>
      <div className="flex items-center justify-between mt-1.5">
        <div className="text-[10.5px] text-stone-500 font-tc-mono truncate">{sub}</div>
        <ArrowUpRight size={12} className="text-stone-400 group-hover:text-[#EA580C] transition-colors shrink-0" />
      </div>
    </Link>
  );
}

function QuickLink({ to, label }) {
  return (
    <Link to={to}
      className="text-[11px] font-tc-mono tracking-widest uppercase px-3.5 py-2 border border-stone-200 bg-white/70 text-stone-700 hover:border-[#EA580C] hover:text-[#EA580C] hover:bg-white transition-all duration-200 rounded-full">
      {label}
    </Link>
  );
}

function AlertStrip({ alerts }) {
  if (!alerts.length) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5" data-testid="alert-strip-clean">
        <div className="text-[10px] font-tc-mono tracking-[0.28em] text-emerald-700">ALERT MATRIX</div>
        <div className="mt-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 tc-glow" />
          <div className="font-tc-display text-[16px] font-bold text-emerald-800">All systems nominal</div>
        </div>
        <div className="text-[11px] text-stone-600 font-tc-mono mt-1">No overdue · no breaches · no low stock</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-5" data-testid="alert-strip">
      <div className="text-[10px] font-tc-mono tracking-[0.28em] text-[#EA580C] flex items-center gap-1.5">
        <AlertTriangle size={10} /> ALERT MATRIX · {alerts.length}
      </div>
      <div className="mt-3 space-y-2">
        {alerts.map((a) => (
          <Link key={a.label} to={a.to} className="flex items-center justify-between text-[13px] group" data-testid={`alert-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="text-stone-700 group-hover:text-[#EA580C] transition-colors">{a.label}</span>
            <span className="font-tc-mono font-bold text-stone-900 flex items-center gap-1 group-hover:text-[#EA580C] transition-colors">
              {a.count} <ChevronRight size={12} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ActionModuleBar({ icon: Icon, label, metric, sub, to, testid, alert }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative flex items-center gap-4 p-4 border ${alert ? "border-orange-300 bg-orange-50/40" : "border-stone-200 bg-white"} rounded-2xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-[#EA580C]/50`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${alert ? "bg-[#EA580C]/10 text-[#EA580C]" : "bg-cyan-50 text-cyan-700"}`}>
        <Icon size={19} strokeWidth={1.7} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-tc-mono tracking-[0.20em] text-stone-500 uppercase">{label}</div>
        <div className="font-tc-display text-[17px] font-bold text-stone-900 truncate">{metric}</div>
        <div className="text-[11px] text-stone-500 font-tc-mono truncate">{sub}</div>
      </div>
      <ChevronRight size={16} className="text-stone-400 group-hover:text-[#EA580C] transition-colors shrink-0" />
    </Link>
  );
}

function buildAlerts(k, budgetsBreach) {
  const out = [];
  if (k.overdue_count > 0)     out.push({ label: "Overdue Invoices",       count: k.overdue_count,     to: "/aging" });
  if (budgetsBreach > 0)       out.push({ label: "Budgets Breached",       count: budgetsBreach,       to: "/finance/budgets" });
  if (k.low_stock_count > 0)   out.push({ label: "Low Stock Items",        count: k.low_stock_count,   to: "/warehouses" });
  if (k.tickets_overdue > 0)   out.push({ label: "SLA Breached Tickets",   count: k.tickets_overdue,   to: "/service/tickets" });
  if (k.warranty_expiring > 0) out.push({ label: "Warranty Expiring Soon", count: k.warranty_expiring, to: "/service/warranty" });
  return out;
}

function CalibratingCore() {
  return (
    <div className="aspect-square w-full max-w-[520px] mx-auto flex flex-col items-center justify-center border-2 border-dashed border-cyan-200 rounded-full relative overflow-hidden bg-white">
      <div className="absolute inset-0 rounded-full"
           style={{ background: "radial-gradient(circle at center, rgba(8,145,178,0.10) 0%, transparent 65%)", filter: "blur(20px)" }} />
      <div className="tc-spin-slow absolute inset-8 border border-cyan-100 rounded-full" style={{ transformOrigin: 'center', transformBox: 'fill-box' }} />
      <div className="tc-spin-rev  absolute inset-16 border border-cyan-100 rounded-full" style={{ transformOrigin: 'center', transformBox: 'fill-box' }} />
      <div className="relative text-center">
        <div className="text-[10px] font-tc-mono tracking-[0.28em] text-cyan-700">SYSTEM STATE</div>
        <div className="font-tc-display text-[22px] font-black text-cyan-800 mt-1 tracking-tight">CALIBRATING</div>
        <div className="text-[11px] text-stone-500 mt-1 font-tc-mono">Post your first invoice · project · GRN</div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="thermal-core-scope tc-paper min-h-screen flex items-center justify-center">
      <div className="text-center relative">
        <div className="tc-laser w-64 h-1 mx-auto bg-stone-200 rounded-full mb-6 relative overflow-hidden" />
        <div className="font-tc-mono text-[11px] tracking-[0.28em] text-stone-500">CALIBRATING…</div>
      </div>
    </div>
  );
}
